import {
  Group,
  Stack,
  MultiSelect,
  NumberInput,
  Select,
  SelectItem,
  TextInput,
  Checkbox,
} from '@mantine/core'
import { useForm, isNotEmpty } from '@mantine/form'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import config from 'app/config/config'
import { useDispatch, useSelector } from 'react-redux'
import React, { forwardRef, useState, useEffect, useRef, useImperativeHandle } from 'react'
import { DealParams, DraftDealParams } from 'app/models/deal-params'
import { createOrEditBulkDeals, loadAllDeals, saveDeal } from 'app/state/ducks/deals/thunks'
import { EntityParams } from 'app/models/entity-params'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import {
  currencyOptions, strBooleanOptions, classificationOptions
} from 'app/models/dropdown-options'
import PrimaryButton from 'app/views/components/buttons/PrimaryButton'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getContacts } from 'app/state/ducks/contacts/selectors'
import moment from 'moment'
import { saveDealAmendment } from 'app/state/ducks/deal-amendments/thunks'
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification'
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { formatDateToUTC, stringToDate } from 'app/utils/util-functions'
import FormWrapper from 'app/views/components/Form/FormWrapper'
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import { EntityRole } from 'app/models/entity-profile-params'
import UploadCSV from 'app/views/components/upload-csv/upload-csv'
import { transformDealCSVToSubmission } from './deals-transformCSV'
import { invalidateDealScreen } from 'app/state/ducks/deal-screen/actions'
import { StableKey } from 'app/models/common-types'

export interface Props {
  deal?: DraftDealParams
  dealAmendment?: DraftDealParams
  amendment?: string | null
  setSaveButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>
  setDisabledSteps: React.Dispatch<React.SetStateAction<number[]>>
  setDeal: React.Dispatch<React.SetStateAction<any>>
  setAmendment: React.Dispatch<React.SetStateAction<any>>
  onClose?: () => void
}

export interface ChildDealRef {
  handleClick: () => void
}

const UpdatedDealsModal: React.ForwardRefRenderFunction<ChildDealRef, Props> = (
  {
    deal,
    amendment,
    dealAmendment,
    setSaveButtonDisabled,
    setDisabledSteps,
    setDeal,
    setAmendment,
    onClose,
  },
  ref
) => {
  const entities = useSelector(getEntities)
  const entityProfiles = useSelector(getEntityProfiles)
  const contacts = useSelector(getContacts)
  const holidayCalendars = useSelector(getCalendars)
  const businessDate = useSelector(getBusinessDate)
  const dispatch = useDispatch()

  const holidayCalendarsOptions = holidayCalendars?.filter(calendar => calendar.status === 'Active').map(calendar => ({ label: calendar.name, value: calendar.id ?? '' }))
  const [loading, setLoading] = useState<boolean>(false)
  const prevIsNameAutoGenerated = useRef<boolean | null>(null) // Track previous state of checkbox for auto-generating deal name
  deal = dealAmendment ? dealAmendment : deal
  const initialIsNameAutoGenerated = deal?.isNameAutoGenerated ?? true

  const transformValues = (values: any) => ({
    ...values,
    agentDealId: values.agentDealId || null,
    isPricingGrid: values.isPricingGrid === 'true',
    amendmentDate: values.amendmentDate ? formatDateToUTC(values.amendmentDate) : undefined,
    startDate: formatDateToUTC(values.startDate),
    maturity: formatDateToUTC(values.maturity),
    closing: formatDateToUTC(values.closing),
    precedentDate: formatDateToUTC(values.precedentDate),
    opsAdmin: Array.isArray(values.opsAdmin) ? values.opsAdmin.map((id: string) => ({
      id,
      admin: contacts.find(contact => contact.id === id)?.maker ?? '',
    })) : [],
    pricingGrid: values.isPricingGrid ? values.pricingGrid : [],
    borrowers: values.borrowers.map((id: string) => ({
      id,
      admin: entities.find(entity => entity.id === id)?.maker,
    })),
    guarantors: values.guarantors.map((id: string) => ({
      id,
      admin: entities.find(entity => entity.id === id)?.maker,
    })),
    holidayCalendar: values.holidayCalendar.map((id: string) => {
      const curCal = holidayCalendars.find((hc) => hc?.id === id)
      return { id, admin: curCal?.agencyAdmin }
    }),
    adminEntity: values.adminEntity.map((id: string) => ({
      id,
      admin: entities.find(entity => entity.id === id)?.maker,
    })),
    whiteLabelAgent: values.whiteLabelAgent && values.whiteLabelAgent !== ''
      ? {
        id: values.whiteLabelAgent,
        admin: entities.find(entity => entity.id === values.whiteLabelAgent)?.maker,
      }
      : null,
    hasError: false,
    hasSuccessfulEntitySave: false,
    ErrorMessage: '',
    customer_token: config.company.toUpperCase(),
    whiteLabelAgentContacts: Array.isArray(values.whiteLabelAgentContacts) ? values.whiteLabelAgentContacts.map((id: string) => ({
      id,
      admin: contacts.find(contact => contact.id === id)?.maker ?? '',
    })) : [],
  })

  const generateDealName = (dynamicNameObj: any) => {
    return `${dynamicNameObj.borrower} (${moment(dynamicNameObj.date).format('YYYY-MMM').toUpperCase()})`
  }

  const amendDate = () => {
    if (amendment === 'edit' && deal?.startDate) {
      return stringToDate(deal?.startDate)
    }
    if (deal?.amendmentDate) {
      return stringToDate(deal.amendmentDate)
    } else {
      return businessDate
    }
  }

  const form = useForm({
    initialValues: {
      dynamicNameObj: {
        borrower:
          entities && deal && deal.borrowers
            ? entities.find(
              entity => entity.id === cleanUUID(deal?.borrowers[0]?.id)
            )?.entityName
            : '',
        amount: deal?.amount ?? '',
        date: deal?.startDate ? stringToDate(deal?.startDate) : businessDate,
        currency: deal?.currency ? deal?.currency : 'USD',
      },
      id: deal?.id ?? undefined,
      transactionId: deal?.transactionId ?? undefined,
      dealName: deal?.dealName ?? '',
      amount: Number(deal?.amount) ?? 0,
      originalAmount: deal?.originalAmount ? Number(deal?.originalAmount) : undefined,
      utilizedAmount: deal?.utilizedAmount ?? 0,
      assignmentFee: deal?.assignmentFee ? Number(deal?.assignmentFee) : undefined,
      currency: deal?.currency ?? '',
      approve: deal?.approve ?? false,
      isPricingGrid: deal?.isPricingGrid == true ? 'true' : 'false',
      holidayCalendar: deal?.holidayCalendar?.map((b: any) => {
        return b.id
      }) ?? [],
      cusip: deal?.cusip,
      isin: deal?.isin,
      lxId: deal?.lxId ?? '',
      bbgId: deal?.bbgId ?? '',
      clientId: deal?.clientId ?? '',
      agentDealId: deal?.agentDealId ?? '',
      borrowers:
        deal?.borrowers?.map((b: any) => {
          return b.id
        }) ?? [],
      guarantors:
        deal?.guarantors?.map((b: any) => {
          return b.id
        }) ?? [],
      adminEntity: deal?.adminEntity?.map((b: any) => {
        return b.id
      }) ?? [],
      whiteLabelAgent: deal?.whiteLabelAgent?.id ?? '',
      startDate: deal?.startDate ? stringToDate(deal.startDate) : businessDate,
      maturity: deal?.maturity ? stringToDate(deal.maturity) : businessDate,
      precedentDate: deal?.precedentDate
        ? stringToDate(deal.precedentDate)
        : businessDate,
      closing: deal?.closing ? stringToDate(deal.closing) : businessDate,
      opsAdmin: deal?.opsAdmin?.map((stableKey: StableKey) => cleanUUID(stableKey.id)) ?? [],
      amendmentDate: amendDate(),
      fees: deal?.fees ?? [],
      pricingGrid: deal?.pricingGrid ?? [],
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase() ?? '',
      classification: deal?.classification ?? null,
      systemId: deal?.systemId ?? null,
      isNameAutoGenerated: initialIsNameAutoGenerated, // use existing deal value (for deal edits) or true (for new deal creation)
      allowNonProRataAssignments: deal?.allowNonProRataAssignments ?? false,
      whiteLabelAgentContacts: deal?.whiteLabelAgentContacts?.map((stableKey: any) => cleanUUID(stableKey.id)) ?? [],
      creditAgreementPreamble: deal?.creditAgreementPreamble ?? '',
    },
    transformValues: values => ({
      ...transformValues(values),
      isAutoGeneratedDealName: values.isNameAutoGenerated,
    }),
    validate: {
      dealName: isNotEmpty('Deal Name cannot be empty'),
      amount: value => (value > 0 ? null : 'Invalid amount'),
      originalAmount: value => {
        if (value === null || value === undefined) return null;
        if (isNaN(Number(value)) || Number(value) <= 0) return 'Invalid amount';
        return null;
      },
      borrowers: value =>
        value && value.length > 0 ? null : 'Borrowers list cannot be empty',
      lxId: value =>
        value == '' || value.length === 8 ? null : 'Invalid amount',
      bbgId: value =>
        value == '' || value.length === 9 ? null : 'Invalid amount',
      classification: value =>
        value ? null : 'Classification is required',
      whiteLabelAgent: value => {
        if (value === '' || value === null || value === undefined) return null;
        return null;
      },
    },
  })

  useEffect(() => {
    setSaveButtonDisabled(!form.isValid())
  }, [form.isValid, setSaveButtonDisabled])

  useEffect(() => {
    const currentIsNameAutoGenerated = form.values.isNameAutoGenerated

    if (currentIsNameAutoGenerated) {
      const newDealName = generateDealName(form.values.dynamicNameObj)
      if (newDealName !== form.values.dealName) {
        form.setFieldValue('dealName', newDealName)
      }
    } else if (
      prevIsNameAutoGenerated.current === true && // Was previously checked
      currentIsNameAutoGenerated === false // Now unchecked
    ) {
      form.setFieldValue('dealName', '') // Clear only on toggle from true to false
    }

    // Update the previous value for the next render
    prevIsNameAutoGenerated.current = currentIsNameAutoGenerated
  }, [
    form.values.isNameAutoGenerated,
    form.values.dynamicNameObj.borrower,
    form.values.dynamicNameObj.amount,
    form.values.dynamicNameObj.date,
    form.values.dynamicNameObj.currency,
  ])


  const onSubmit = async (dealValues: any) => {
    setLoading(true)
    try {
      const submissionValues = form.getTransformedValues()
      let response: any = null

      if (amendment) {
        response = await dispatch(saveDealAmendment(submissionValues, dealAmendment?.id ?? null))
        setAmendment(response.payload)
      } else {
        response = await dispatch(saveDeal(submissionValues, !!dealValues.id))
        setDeal(response.payload)
      }

      setLoading(false)
      setDisabledSteps(submissionValues.isPricingGrid ? [3, 4, 5, 6, 7, 8, 9, 10] : [2, 3, 4, 5, 6, 7, 8, 9, 10])

      if (response.success) {
        SuccessNotification({
          title: 'Successful Deal Creation',
          message: 'You can add a facility to your deal by clicking on edit deal.',
        })
      } else {
        ErrorNotification({
          title: 'Failed Deal Creation',
          message: response.payload ?? 'Please check all fields and resubmit.',
        })
      }
    } catch {
      setLoading(false)
      ErrorNotification({
        title: 'Deal Save Failed',
        message: 'An error occurred while saving the deal. Please try again.',
      })
    }
  }

  const handleClick = async () => {
    await onSubmit(form.values)
  }

  // Expose the handleClick method to the parent component
  useImperativeHandle(ref, () => ({
    handleClick,
  }))

  const borrowers: SelectItem[] = entities
    ?.filter(entity =>
      entity.entityProfile.some(profile => entityProfiles.find(option => option.id === profile.id)?.roles.includes(EntityRole.BorrowerRole)))
    .map(entity => ({ label: entity.entityName, value: entity.id ?? '' }))

  const guarantors: SelectItem[] = entities
    ?.filter(entity =>
      entity.entityProfile.some(profile => entityProfiles.find(option => option.id === profile.id)?.roles.includes(EntityRole.GuarantorRole)))
    .map(entity => ({ label: entity.entityName, value: entity.id ?? '' }))

  const adminAgents: SelectItem[] = entities
    ?.filter(entity =>
      entity.entityProfile.some(profile =>
        entityProfiles.find(option => option.id === profile.id)?.roles.includes(EntityRole.AdminAgentRole)
      )
    )
    .map((entity: EntityParams) => ({
      label: entity.mei ? `${entity.entityName} - ${entity.mei}` : entity.entityName,
      value: entity.id ?? '',
    }))

  const whiteLabelAgentOptions: SelectItem[] = [
    { label: 'None', value: '' },
    ...adminAgents
  ]

  const filterOpsAdminContact = (e: string[]) => {
    form.setFieldValue('opsAdmin', [])
    if (e.length <= 0) {
      form.setFieldValue('adminEntity', [])
    } else {
      form.setFieldValue('adminEntity', e)
    }
  }

  const filterWhiteLabelAgentContacts = (value: string | null) => {
    form.setFieldValue('whiteLabelAgent', value ?? '');
    form.setFieldValue('whiteLabelAgentContacts', []);
  };

  const updateDynamicName = (
    key: keyof typeof form.values.dynamicNameObj,
    value: string | any
  ) => {
    const dynaobj = { ...form.values.dynamicNameObj } // Avoid mutating directly
    if (key === 'borrower') {
      const entityName =
        entities.find(entity => entity.id === cleanUUID(value))?.entityName ?? ''
      dynaobj['borrower'] = entityName
    } else {
      dynaobj[key] = value
    }
    form.setFieldValue('dynamicNameObj', dynaobj)
    if (form.values.isNameAutoGenerated) {
      const newDealName = generateDealName(dynaobj)
      if (newDealName !== form.values.dealName) {
        form.setFieldValue('dealName', newDealName)
      }
    }
  }

  const handleCurrencyChange = (e: string) => {
    form.setFieldValue('currency', e)
    updateDynamicName('currency', e)
  }

  const handleAmountChange = (e: number) => {
    form.setFieldValue('amount', e)
    updateDynamicName('amount', e)
  }

  const handleOriginalAmountChange = (e: number) => {
    form.setFieldValue('originalAmount', e)
  }

  const handleAssignmentFee = (e: number) => {
    form.setFieldValue('assignmentFee', e)
  }

  const handleBorrowersChange = (e: string[]) => {
    if (e.length <= 0) {
      form.setFieldValue('borrowers', [])
      form.setFieldValue('borrowerPaymentInstructions', [])
      updateDynamicName('borrower', '')
    } else {
      form.setFieldValue('borrowers', e)
      updateDynamicName('borrower', e[0])
    }
  }

  const handleCSVData = async (parsedData: Record<string, string>[]) => {
    // the import matches rows to existing deals: the deal list is loaded here, not by the page
    const loaded: any = await dispatch(loadAllDeals())
    if (!Array.isArray(loaded?.payload)) {
      // without the list every row would be imported as a NEW deal
      ErrorNotification({
        title: 'Import Not Started',
        message: 'The deal list could not be loaded, so the rows could not be matched to existing deals. Try again.',
      })
      return
    }
    const dealList: DealParams[] = loaded.payload
    const transformedDeals = transformDealCSVToSubmission(parsedData, dealList, entities, contacts, holidayCalendars, businessDate, transformValues)
    const result: any = await dispatch(createOrEditBulkDeals(transformedDeals))
    if (!result?.success) {
      // the form stays open with the parsed rows: nothing was saved
      ErrorNotification({
        title: 'Import Failed',
        message: typeof result?.payload === 'string' ? result.payload : 'The deals could not be saved.',
      })
      return
    }
    handleBulkNotificationAndClosure()
  }

  async function handleBulkNotificationAndClosure() {
    SuccessNotification({
      title: 'Successfully Saved Bulk Deals',
      message: 'You created many deals',
    })
    dispatch(loadAllDeals())
    // the import answers with no records to patch the Deals grid from
    dispatch(invalidateDealScreen())
    if (onClose) { onClose() }
  }


  return (
    <FormWrapper title=''>
      <Stack>
        <Group noWrap w="100%" position='apart'>
          <h4 className='form-wrapper-title'>{deal?.dealName ? 'Edit' : 'Create'} Deal</h4>
          <UploadCSV onParse={handleCSVData} />
        </Group>
        {amendment && (
          <Group style={{ paddingTop: '0px' }}>
            <CustomDatePicker
              label={'Amendment Date'}
              date={form.values.amendmentDate ? form.values.amendmentDate : businessDate}
              setDate={(value: any) => {
                form.setFieldValue('amendmentDate', value)
              }}
              holidayCalendars={
                deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
              }
              disabled={amendment != null && amendment === 'edit' ? true : false}
            />
          </Group>
        )}
        <Group w="100%" grow pb={5}>
          <Stack style={{ paddingTop: '0px' }}>
            <Group w='100%' spacing="xs">
              <label htmlFor="dealName" style={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px', fontWeight: 500, color: '#212529', lineHeight: '1.55', marginRight: '10px' }}>
                Deal Name
              </label>
              <Checkbox
                label="Auto-Generate"
                checked={form.values.isNameAutoGenerated}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  form.setFieldValue('isNameAutoGenerated', event.currentTarget.checked)
                }
              />
            </Group>
            <TextInput
              disabled={form.values.isNameAutoGenerated}
              name="dealName"
              id="dealName"
              w='100%'
              placeholder={form.values.isNameAutoGenerated
                ? "Deal Name is Auto-Generated"
                : "Enter Custom Deal Name"}
              {...form.getInputProps('dealName')}
            />
          </Stack>
          <Select
            searchable
            w='100%'
            label="Currency"
            placeholder="Select a Currency"
            data={currencyOptions ?? []}
            onChange={(e: string) => {
              handleCurrencyChange(e)
            }}
            value={form.values.currency}
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <TextInput
            label="Client ID"
            name="clientId"
            id="clientId"
            placeholder="Enter Client ID"
            {...form.getInputProps('clientId')}
            onChange={(e) => form.setFieldValue('clientId', e.target.value.toUpperCase())}
          />
          <TextInput
            label="Agent Deal ID"
            name="agentDealId"
            id="agentDealId"
            placeholder="Enter Agent Deal ID"
            {...form.getInputProps('agentDealId')}
          />
          <TextInput
            label="Deal CUSIP"
            name="cusip"
            id="cusip"
            placeholder="Enter CUSIP"
            minLength={9}
            maxLength={9}
            disabled={amendment != null ? true : false}
            {...form.getInputProps('cusip')}
            onChange={(e) => form.setFieldValue('cusip', e.target.value.toUpperCase())}
            error={
              !form.values.cusip || form.values.cusip.length == 9
                ? null
                : 'Cusip Should Be 9 Characters'
            }
          />
          <TextInput
            label="Deal ISIN"
            name="isin"
            id="isin"
            placeholder="Enter ISIN"
            minLength={12}
            maxLength={12}
            disabled={amendment != null ? true : false}
            {...form.getInputProps('isin')}
            onChange={(e) => form.setFieldValue('isin', e.target.value.toUpperCase())}
            error={
              !form.values.isin || form.values.isin.length == 12
                ? null
                : 'ISIN Should Be 12 Characters'
            }
          />
          <TextInput
            label="Deal LXID"
            name="lxId"
            id="lxId"
            placeholder="Enter Deal LXID"
            minLength={8}
            maxLength={8}
            disabled={amendment != null ? true : false}
            {...form.getInputProps('lxId')}
            onChange={(e) => form.setFieldValue('lxId', e.target.value.toUpperCase())}
            error={
              !form.values.lxId || form.values.lxId.length == 8
                ? null
                : 'LXID Should Be 8 Characters'
            }
          />
          <TextInput
            label="Deal BBG ID"
            name="bbgId"
            id="bbgId"
            placeholder="Enter Deal Bloomberg Id"
            minLength={9}
            maxLength={9}
            disabled={amendment != null ? true : false}
            {...form.getInputProps('bbgId')}
            onChange={(e) => form.setFieldValue('bbgId', e.target.value.toUpperCase())}
            error={
              !form.values.bbgId || form.values.bbgId.length == 9
                ? null
                : 'BBG ID Should Be 9 Characters'
            }
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <MultiSelect
            searchable
            placeholder="Select Administrative Agent(s)"
            label="Administrative Agent(s)"
            name="Administrative Agent"
            data={adminAgents ?? []}
            onChange={e => filterOpsAdminContact(e)}
            value={form.values.adminEntity}
          />
          <Select
            searchable
            placeholder="Select White Label Agent"
            label="White Label Agent"
            name="whiteLabelAgent"
            data={whiteLabelAgentOptions}
            onChange={filterWhiteLabelAgentContacts}
            value={form.values.whiteLabelAgent ?? ''}
            clearable
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <MultiSelect
            placeholder="Select Administrative Agent Contact(s)"
            searchable
            label="Administrative Agent Contact(s)"
            data={
              contacts && contacts.length > 0
                ? contacts
                  .filter(contact =>
                    contact.entity &&
                    Array.isArray(form.values.adminEntity) &&
                    form.values.adminEntity.some(
                      adminEntity =>
                        contact.entity.id.replace(/['"]/g, '') === adminEntity
                    )
                  )
                  .map(contact => ({
                    value: contact.id ?? '',
                    label: contact.name,
                  }))
                : []
            }
            {...form.getInputProps('opsAdmin')}
          />
          <MultiSelect
            searchable
            placeholder="Select White Label Agent Contact(s)"
            label="White Label Agent Contact(s)"
            data={
              contacts && contacts.length > 0 && form.values.whiteLabelAgent
                ? contacts
                  .filter(contact =>
                    contact.entity &&
                    contact.entity.id.replace(/['"]/g, '') === form.values.whiteLabelAgent
                  )
                  .map(contact => ({
                    value: contact.id ?? '',
                    label: contact.name,
                  }))
                : []
            }
            {...form.getInputProps('whiteLabelAgentContacts')}
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <NumberInput
            pb={5}
            label="Original Amount"
            id="originalAmount"
            placeholder="Enter Original Amount"
            precision={2}
            parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? ''))
                ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            onChange={(e: number) => {
              handleOriginalAmountChange(e)
            }}
            value={form.values.originalAmount || undefined}
          />
          <NumberInput
            withAsterisk
            pb={5}
            label="Current Amount"
            id="amount"
            placeholder="Enter Current Amount"
            precision={2}
            parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? ''))
                ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            onChange={(e: number) => {
              handleAmountChange(e)
            }}
            value={form.values.amount}
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <CustomDatePicker
            label={'Start Date'}
            date={form.values.startDate}
            setDate={(value: any) => {
              form.setFieldValue('startDate', value)
              updateDynamicName('date', value)
              if (amendment === 'edit') {
                form.setFieldValue('amendmentDate', value)
              }
            }}
            holidayCalendars={
              deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
            }
          />
          <CustomDatePicker
            label={'Maturity Date'}
            date={form.values.maturity}
            setDate={(value: any) => form.setFieldValue('maturity', value)}
            holidayCalendars={
              deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
            }
          />
        </Group>
        <Group grow pb={5}>
          <MultiSelect
            searchable
            withAsterisk
            placeholder="Select Borrower(s)"
            label="Borrower(s)"
            name="borrower"
            data={borrowers ?? []}
            onChange={e => handleBorrowersChange(e)}
            value={form.values.borrowers}
          />
        </Group>
        <Group grow pb={5}>
          <MultiSelect
            searchable
            placeholder="Select Guarantor(s)"
            label="Guarantor(s)"
            data={guarantors ?? []}
            {...form.getInputProps('guarantors')}
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <Select
            withAsterisk
            placeholder="Select Classification"
            searchable
            label="Classification"
            data={classificationOptions ?? []}
            {...form.getInputProps('classification')}
          />
          <NumberInput
            pb={5}
            label="Assignment Fee"
            id="assignmentFee"
            placeholder="Enter Assignment Fee"
            parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
            formatter={value =>
              !Number.isNaN(parseFloat(value ?? ''))
                ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                : ''
            }
            onChange={(e: number) => {
              handleAssignmentFee(e)
            }}
            value={form.values.assignmentFee || undefined}
          />
        </Group>
        <Group w='100%' grow pb={5}>
          <Select
            clearable
            searchable
            placeholder="Select yes or no"
            label="Pricing Grid"
            data={strBooleanOptions}
            {...form.getInputProps('isPricingGrid')}
          />
          <MultiSelect
            searchable
            label="Holiday Calendar(s)"
            placeholder="Select Holiday Calendar(s)"
            data={holidayCalendarsOptions ?? []}
            {...form.getInputProps('holidayCalendar')}
          />
        </Group>
        <Group grow pb={5}>
          <CustomDatePicker
            label={'Date Conditions Precedent Met'}
            date={form.values.precedentDate}
            setDate={(value: any) => form.setFieldValue('precedentDate', value)}
            holidayCalendars={
              deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
            }
          />
        </Group>
        <Group
          style={{ marginTop: 20, marginBottom: 20, display: 'none' }}
        >
          <PrimaryButton
            onClick={handleClick}
            className="form-button"
            loading={loading}
            w="100%"
          >
            Save and Proceed
          </PrimaryButton>
        </Group>
      </Stack>
    </FormWrapper>
  )
}

export default forwardRef(UpdatedDealsModal)
